import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css, useTheme } from 'styled-components'

import useAirportSearch from '../../hooks/airport-search'
import useSearch from '../../hooks/search'
import useDates from '../../hooks/dates'

import Autocomplete from '../ui/autocomplete'
import AirportsList from './AirportList'
import Popover from '../ui/popover'
import Button from '../ui/button'
import Single from '../ui/datePicker/Single'
import Range from '../ui/datePicker/Range'
import { RadioGroup, Radio } from '../ui/radio'
import Passengers from '../search/Passengers'

const StyledForm = styled.form`
  ${({ theme }) => css`
    background: ${theme.bgColor};
    padding: 30px;
    border-radius: 5px;
    position: relative;
  `}
`

const StyledRadio = styled(Radio)`
  ${(props) => css`
    font-weight: 600 !important;
  `}
`

const StyledButton = styled(Button)`
  ${(props) => css`
    background: ${props.theme.mainColor} !important;
    color: white !important;
    text-align: center !important;
    &:hover {
      background: ${props.theme.secondaryColor} !important;
    }
  `}
`
const StyledInput = styled(Button)`
  ${(props) =>
    props.isFocused &&
    css`
      box-shadow: var(--tw-ring-inset) 0 0 0
        calc(1px + var(--tw-ring-offset-width)) ${props.theme.mainColor} !important;
    `}
`

const ListContainer = styled.div`
  ${(props) =>
    props.hasItems &&
    css`
      box-shadow: var(--tw-ring-inset) 0 0 0
        calc(1px + var(--tw-ring-offset-width)) ${props.theme.mainColor} !important;
    `}
`

const ValueSpan = styled.span`
  ${(props) => css`
    color: ${props.theme.secondaryColor} !important;
  `}
`

function AirportAutoComplete({
  id,
  label,
  className,
  onSelect,
  selectedItem,
  error,
  isSecondary,
}) {
  const searchAirports = useAirportSearch()
  const theme = useTheme()
  return (
    <Autocomplete
      id={id}
      label={label}
      className={className}
      onChange={onSelect}
      search={searchAirports}
      selectedItem={selectedItem}
      error={error}
      noBorder
      noShadow
      inputClassName={isSecondary ? 'text-dark' : `text-primary`}
    >
      {({ items, highlightedIndex }) => (
        <ListContainer
          hasItems={items.length > 0}
          theme={theme}
          className={`${
            items.length > 0 && 'ring-2'
          } ul-absolute ul-left-0 ul-right-0 ul-bg-white ul-mt-5 ul-rounded-5 ul-shadow-double ul-max-h-300 ul-overflow-auto ul-z-10`}
        >
          <AirportsList list={items} highlightedIndex={highlightedIndex} />
        </ListContainer>
      )}
    </Autocomplete>
  )
}

function findAirportByIata(iata, hits) {
  if (hits.length == 0) {
    return null
  }

  return hits.find((item) => item.value == iata) || hits[0]
}

export default function SearchForm({
  partner,
  origin,
  destination,
  departureDate,
  returnDate,
  tripType,
  affilaeId,
}) {
  const { t, i18n } = useTranslation()
  const { formatDate } = useDates()
  const search = useSearch(null, tripType)
  const searchAirports = useAirportSearch()
  const destinationRef = useRef()
  const datesRef = useRef()
  const theme = useTheme()
  const [isFocused, setIsFocused] = useState(false)
  const [isFocusedButton, setIsFocusedButton] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [showErrors, setShowErrors] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const sliceId = search.slices[0].id
  const dates = search.getDates(sliceId)
  const minDate = search.getMinDate(sliceId)
  const showDateError =
    showErrors &&
    (search.errors.slices.find((s) => s.id === sliceId)?.date ||
      search.errors.missingReturnDate)

  useEffect(async () => {
    if (departureDate && returnDate) {
      search.setDates([new Date(departureDate), new Date(returnDate)])
    } else if (departureDate) {
      search.setDates([new Date(departureDate)], sliceId)
    }

    if (origin) {
      const origins = await searchAirports(origin)
      const airport = findAirportByIata(origin, origins)
      if (airport) search.setOrigin(airport, sliceId)
    }

    if (destination) {
      const destinations = await searchAirports(destination)
      const airport = findAirportByIata(destination, destinations)
      if (airport) search.setDestination(airport, sliceId)
    }
  }, [origin, destination, departureDate, returnDate, tripType])

  return (
    <StyledForm
      className="ulysse-prefix ul-font-normal"
      theme={theme}
      name="ulyssewidget"
    >
      <div className="ul-mb-20 ul-font-semibold">
        <RadioGroup aria-labelledby="triptype" defaultValue="roundtrip">
          <StyledRadio
            value="roundtrip"
            onClick={() => search.setTripType('roundtrip')}
            selected={search.tripType === 'roundtrip'}
          >
            {t('components.triptype.roundtrip')}
          </StyledRadio>
          <StyledRadio
            value="oneway"
            onClick={() => search.setTripType('oneway')}
            selected={search.tripType === 'oneway'}
          >
            {t('components.triptype.oneway')}
          </StyledRadio>
        </RadioGroup>
      </div>
      <AirportAutoComplete
        isSecondary={false}
        id={`origin-${sliceId}`}
        key={`origin-${sliceId}`}
        label={t('components.searchForm.origin')}
        className={`ul-border ul-border-gray-light`}
        onSelect={(val) => {
          search.setOrigin(val, sliceId)
          if (val && destinationRef.current) {
            destinationRef.current.focus()
          }
        }}
        selectedItem={search.slices.find((s) => s.id === sliceId)?.origin}
        error={
          showErrors &&
          search.errors.slices.find((s) => s.id === sliceId)?.origin
        }
      />
      <AirportAutoComplete
        isSecondary={false}
        id={`destination-${sliceId}`}
        key={`destination-${sliceId}`}
        label={t('components.searchForm.destination')}
        className="ul-border ul-border-gray-light ul-mt-10"
        onSelect={(val) => {
          search.setDestination(val, sliceId)
          if (val && datesRef.current) {
            datesRef.current.click()
          }
        }}
        selectedItem={search.slices.find((s) => s.id === sliceId)?.destination}
        error={
          showErrors &&
          search.errors.slices.find((s) => s.id === sliceId)?.destination
        }
      />
      <Popover
        setIsFocused={setIsFocused}
        overlayClassName="ulysse-search-form"
        trigger={
          <StyledInput
            onClick={() => setIsFocused(!isFocused)}
            isFocused={isFocused}
            unstyled
            className={`ul-w-full ul-h-60 ul-px-20 ul-mt-10 ul-text-left ul-rounded-5 ul-bg-white ul-border ul-border-gray-light ${
              showDateError && 'ul-ring-2 ul-ring-danger'
            }`}
            aria-labelledby={`dates-label-${sliceId}`}
            aria-describedby={`dates-description-${sliceId}`}
            aria-invalid={!!showDateError}
            type="button"
          >
            <span
              id={`dates-label-${sliceId}`}
              className={`block ${
                dates.length ? 'ul-text-12' : 'ul-text-base'
              } ul-text-dark-60 ul-leading-none`}
            >
              {search.tripType === 'roundtrip'
                ? t('components.searchForm.dates')
                : t('components.searchForm.date')}
            </span>
            <ValueSpan
              id={`dates-description-${sliceId}`}
              className="ul-block ul-font-medium ul-truncate"
            >
              {search.tripType === 'roundtrip'
                ? dates.map((date) => formatDate(date, 'E d MMM')).join(' - ')
                : dates[0]
                ? formatDate(dates[0], 'E d MMM')
                : null}
            </ValueSpan>
          </StyledInput>
        }
      >
        {({ transitionState, contentStyle, close }) => {
          if (search.tripType === 'roundtrip') {
            return (
              <Range
                isOpen
                selectedDates={dates}
                onSelectedDatesChange={(val, id) => {
                  search.setDates(val, id)
                  if (val.length === 2) {
                    close()
                  }
                }}
              />
            )
          } else {
            return (
              <Single
                isOpen
                selectedDate={dates[0]}
                minDate={minDate}
                onSelectedDatesChange={(val) => {
                  search.setDates(val, sliceId)
                  close()
                }}
              />
            )
          }
        }}
      </Popover>
      {showDateError ? (
        <p className="ul-text-danger ul-text-14 ul-mt-5">
          {search.errors.slices.find((s) => s.id === sliceId)?.date ||
            search.errors.missingReturnDate}
        </p>
      ) : null}
      <Popover
        overlayClassName="ulysse-search-form"
        setIsFocused={setIsFocusedButton}
        trigger={
          <StyledInput
            onClick={() => setIsFocusedButton(!isFocusedButton)}
            isFocused={isFocusedButton}
            unstyled
            className="ul-relative ul-w-full ul-h-60 ul-px-20 ul-text-left ul-rounded-5 ul-border ul-border-gray-light ul-bg-white ul-mt-10"
            type="button"
            aria-label={`${search.longPassengersCabinClassDisplay}. ${t(
              'components.formDesktop.travelersAriaLabel'
            )}`}
          >
            <span
              id="passengers-label"
              className="ul-block ul-text-12 ul-text-dark-60 ul-leading-none"
            >
              {t('components.searchForm.travelersAndClass')}
            </span>
            <ValueSpan className="ul-block ul-font-medium ul-pr-10 ul-truncate">
              {search.shortPassengersCabinClassDisplay}
            </ValueSpan>
            <span className="ul-text-dark-60">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="ul-w-12 ul-absolute ul-right-20 ul-top-1/2 ul-transform -ul-translate-y-1/2"
              >
                <title>chevron</title>
                <path
                  d="M12,17.61,3.44,10.9A1.5,1.5,0,1,1,5.29,8.54L12,13.8l6.71-5.26a1.5,1.5,0,1,1,1.85,2.36Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </StyledInput>
        }
      >
        <Passengers
          numAdults={search.numAdults}
          numChildren={search.numChildren}
          numBabies={search.numBabies}
          onNumAdultsChange={search.setNumAdults}
          onNumChildrenChange={search.setNumChildren}
          onNumBabiesChange={search.setNumBabies}
          cabinClass={search.cabinClass}
          onCabinClassChange={search.setCabinClass}
        />
      </Popover>
      <StyledButton
        className="ul-w-full ul-h-60 ul-mt-10 ul-border ul-rounded-5 ul-border-transparent"
        theme={theme}
        size="xl"
        onClick={() => {
          setShowErrors(false)
          if (search.isValid) {
            setIsSubmitting(true)
            var windowReference = window.open(undefined, `ulysse_${partner}`)

            search
              .createSearch({ partner })
              .then(({ data }) => {
                if (data?.id) {
                  var affilaeTrackingCode = !!affilaeId ? `#${affilaeId}` : ''
                  windowReference.location = `${process.env.REACT_APP_MOKA}/${i18n.language}/search/${data.id}?partnerId=${partner}&utm_medium=widget${affilaeTrackingCode}`

                  windowReference.focus()
                }
                setIsSubmitting(false)
              })
              .catch((err) => {
                console.error(err)
                setIsSubmitting(false)
              })
          } else {
            setShowErrors(true)
          }
        }}
        loading={isSubmitting}
      >
        {t('components.searchForm.button')}
      </StyledButton>
      <div className="ul-flex ul-items-center ul-mt-20">
        <p className="ul-text-[#0f2041] ul-font-bold ul-text-12 ul-mt-5 ul-mr-10">
          Powered by
        </p>
        <svg
          width="68"
          height="24"
          viewBox="0 0 68 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.729 23.999H17.6475V20.9482H17.729C18.2304 20.9516 18.7247 20.8312 19.1678 20.5977C19.6108 20.3642 19.9887 20.0249 20.2675 19.6103L15.9981 9.32673H19.4258L21.8869 15.2575L23.9704 9.32939L27.3287 9.32607L23.5923 19.9606C23.1564 21.1543 22.3588 22.184 21.3097 22.9075C20.2605 23.631 19.0115 24.0126 17.735 23.9997L17.729 23.999ZM47.5281 18.4824C46.5443 17.4565 45.9937 16.0941 45.99 14.6762C45.9863 13.2583 46.5296 11.8931 47.508 10.8621C48.023 10.3506 48.6384 9.95024 49.3159 9.68596C49.9933 9.42168 50.7183 9.29917 51.4454 9.32607C52.1382 9.3055 52.8274 9.43182 53.4674 9.69665C54.1073 9.96148 54.6833 10.3588 55.157 10.8621C55.6523 11.3617 56.0405 11.9561 56.298 12.6094C56.5556 13.2627 56.6772 13.9612 56.6554 14.6626C56.6564 15.0701 56.615 15.4766 56.5318 15.8756L49.2503 15.8789C49.5996 16.828 50.42 17.2933 51.712 17.2933C52.0763 17.3122 52.4405 17.2534 52.7801 17.1209C53.1198 16.9884 53.427 16.7852 53.6813 16.525L56.1417 17.9154C55.6311 18.6065 54.9544 19.1591 54.1737 19.5228C53.393 19.8865 52.533 20.0495 51.6726 19.9971C50.9151 20.0328 50.1581 19.9171 49.4463 19.657C48.7345 19.3968 48.0822 18.9974 47.5281 18.4824ZM49.1875 13.6151H53.5965C53.4926 13.1309 53.2141 12.7013 52.8136 12.4074C52.4131 12.1136 51.9183 11.9757 51.4227 12.0199C50.9187 11.9687 50.4132 12.1026 50.0015 12.3964C49.5898 12.6902 49.3003 13.1236 49.1875 13.6151ZM35.9931 17.5931L38.6539 16.0969C38.7621 16.4646 38.9944 16.784 39.3116 17.0013C39.6288 17.2186 40.0116 17.3206 40.3954 17.29C41.1303 17.29 41.501 17.0906 41.501 16.6639C41.501 16.2598 40.6994 16.0172 39.7187 15.7746C38.3052 15.3884 36.4621 14.7031 36.4621 12.6414C36.4422 12.172 36.5362 11.7048 36.7359 11.2791C36.9356 10.8534 37.2353 10.4817 37.6097 10.1954C38.4416 9.59686 39.4503 9.29119 40.4763 9.32673C41.2751 9.30205 42.0662 9.48946 42.7682 9.86968C43.4701 10.2499 44.0577 10.8092 44.4705 11.4902L41.8491 12.886C41.7295 12.6226 41.5366 12.3989 41.2932 12.2413C41.0498 12.0836 40.766 11.9988 40.4756 11.9967C39.9024 11.9967 39.6145 12.1788 39.6145 12.5629C39.6145 12.9664 40.4162 13.1884 41.3988 13.4516C42.8117 13.7547 44.6762 14.4831 44.6555 16.6858C44.6817 17.1703 44.5824 17.6533 44.3672 18.0887C44.1519 18.5241 43.828 18.8972 43.4263 19.1723C42.5229 19.7457 41.4665 20.0344 40.3954 20.0004C38.2457 19.9985 36.7707 19.1902 35.9931 17.5931ZM26.6606 17.5931L29.3227 16.0969C29.431 16.4646 29.6633 16.784 29.9805 17.0013C30.2977 17.2186 30.6804 17.3206 31.0643 17.29C31.7992 17.29 32.1699 17.0906 32.1699 16.6639C32.1699 16.2598 31.3683 16.0172 30.3876 15.7746C28.9781 15.3884 27.1316 14.7031 27.1316 12.6414C27.1117 12.1721 27.2054 11.705 27.4049 11.2793C27.6044 10.8536 27.9038 10.4819 28.2779 10.1954C29.1104 9.59709 30.1194 9.29145 31.1458 9.32673C31.9446 9.30196 32.7356 9.48933 33.4375 9.86957C34.1394 10.2498 34.7268 10.8092 35.1393 11.4902L32.518 12.886C32.3985 12.6226 32.2056 12.3988 31.9621 12.2411C31.7187 12.0835 31.4349 11.9987 31.1445 11.9967C30.5713 11.9967 30.2834 12.1788 30.2834 12.5629C30.2867 12.9664 31.085 13.1884 32.0677 13.4516C33.4773 13.7547 35.3458 14.4831 35.3257 16.6858C35.3516 17.1703 35.2522 17.6533 35.0368 18.0887C34.8215 18.524 34.4975 18.8971 34.0959 19.1723C33.1925 19.7457 32.1361 20.0344 31.065 20.0004C28.9172 19.9985 27.4396 19.1902 26.6613 17.5931H26.6606ZM11.3306 19.9984V5.32218H14.6634V19.9971L11.3306 19.9984ZM0 15.1465V9.3254H3.13107V15.1465C3.1187 15.476 3.19721 15.8026 3.35806 16.0909C3.51891 16.3793 3.75598 16.6183 4.04361 16.7822C4.33483 16.9499 4.66539 17.0383 5.00191 17.0383C5.33842 17.0383 5.66899 16.9499 5.96021 16.7822C6.24784 16.6183 6.48491 16.3793 6.64576 16.0909C6.80661 15.8026 6.88511 15.476 6.87275 15.1465V9.3254H10.0005V15.1465C9.9703 16.4456 9.43038 17.6815 8.49619 18.5897C7.562 19.4979 6.30771 20.0064 5.00158 20.0064C3.69545 20.0064 2.44116 19.4979 1.50697 18.5897C0.572786 17.6815 0.0328627 16.4456 0.00267067 15.1465H0ZM63.4226 14.3236L59.0136 10.9046C58.6819 10.6305 58.4716 10.2377 58.428 9.81084C58.3844 9.38397 58.5109 8.95714 58.7805 8.62219L60.8019 6.06924C60.8754 5.97553 60.7826 5.84326 60.679 5.89311L55.9032 8.20811C55.7075 8.30479 55.4916 8.35411 55.2732 8.35203C55.0547 8.34995 54.8398 8.29654 54.646 8.19615L51.1562 6.3843C50.9901 6.28711 50.8556 6.1446 50.7685 5.97366C50.6814 5.80272 50.6454 5.61051 50.6647 5.41982C50.684 5.22913 50.7579 5.04796 50.8776 4.89777C50.9972 4.74758 51.1577 4.63468 51.3399 4.57244L66.217 0.0474592C66.4525 -0.0148885 66.7002 -0.0158214 66.9361 0.0447514C67.1721 0.105324 67.3883 0.225364 67.5641 0.393286C67.7398 0.561208 67.8692 0.771347 67.9395 1.00345C68.0099 1.23555 68.019 1.48179 67.9659 1.71841L66.1122 13.1199C66.0571 13.5379 65.8542 13.9228 65.5398 14.2055C65.2255 14.4882 64.8202 14.6502 64.3966 14.6626C64.0428 14.6621 63.6996 14.5427 63.4226 14.3236Z"
            fill="#0F2041"
            fill-opacity="0.8"
          />
        </svg>
      </div>
    </StyledForm>
  )
}
